import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const convertTo12Hour = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const formatTimeSlots = (slots) => {
  if (!slots || slots.length === 0) return "Off";
  return slots.map(s => `${convertTo12Hour(s.start_time)}-${convertTo12Hour(s.end_time)}`).join(", ");
};

export default function WorkerAvailabilityCard({
  worker,
  availability,
  onToggleAvailable,
  onAddSlot,
  onUpdateSlot,
  onDeleteSlot,
  saving
}) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [newSlot, setNewSlot] = useState({ day: null, startTime: '09:00', endTime: '17:00' });
  const [editingSlot, setEditingSlot] = useState(null);
  const [editSlotData, setEditSlotData] = useState({ startTime: '', endTime: '' });

  const handleAddSlot = async (dayIndex) => {
    await onAddSlot(worker.id, dayIndex, newSlot);
    setNewSlot({ day: null, startTime: '09:00', endTime: '17:00' });
    setEditingAvailability(null);
  };

  const handleUpdateSlot = async (slotId) => {
    await onUpdateSlot(worker.id, slotId, editSlotData);
    setEditingSlot(null);
    setEditSlotData({ startTime: '', endTime: '' });
  };

  return (
    <Box
      p={{ base: 4, md: 5 }}
      borderRadius="xl"
      bg="white"
      border="2px solid"
      borderColor={worker.available ? "green.200" : "gray.200"}
      opacity={worker.available ? 1 : 0.6}
      boxShadow="sm"
    >
      {/* Worker Header */}
      <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={3} mb={4}>
        <Heading size="sm">{worker.name}</Heading>
        <HStack>
          <Text fontSize="sm" fontWeight="medium">
            {worker.available ? "Available" : "Unavailable"}
          </Text>
          <Switch
            colorScheme="green"
            size="lg"
            isChecked={worker.available}
            aria-label={`Toggle ${worker.name} availability`}
            onChange={() => onToggleAvailable(worker.id, worker.available)}
          />
        </HStack>
      </Flex>

      {worker.available && (
        <>
          {/* Weekly Schedule Grid */}
          <Box mb={4}>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 7 }} spacing={2}>
              {DAYS.map((day, dayIndex) => {
                const slots = availability[dayIndex] || [];
                const isOff = slots.length === 0;

                return (
                  <VStack key={dayIndex} spacing={1} align={{ base: "stretch", md: "center" }}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                      {day.slice(0, 3)}
                    </Text>
                    <Box
                      p={2}
                      bg={isOff ? "gray.100" : "green.50"}
                      borderRadius="md"
                      fontSize="xs"
                      textAlign="center"
                      minH="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="xs" fontWeight="medium">
                        {formatTimeSlots(slots)}
                      </Text>
                    </Box>
                  </VStack>
                );
              })}
            </SimpleGrid>
          </Box>

          {/* Edit Schedule Button */}
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            width="full"
            leftIcon={<EditIcon />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide Schedule" : "Edit Schedule"}
          </Button>

          {/* Expanded Schedule Editor */}
          {expanded && (
            <Box mt={4} p={4} bg="gray.50" borderRadius="lg">
              <VStack spacing={4} align="stretch">
                {DAYS.map((day, dayIndex) => {
                  const slots = availability[dayIndex] || [];

                  return (
                    <Box key={dayIndex}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontWeight="semibold" fontSize="sm">{day}</Text>
                        <IconButton
                          icon={<AddIcon />}
                          size="xs"
                          colorScheme="blue"
                          aria-label={`Add ${day} hours for ${worker.name}`}
                          onClick={() => {
                            setEditingAvailability({ workerId: worker.id, day: dayIndex });
                            setNewSlot({ day: dayIndex, startTime: '09:00', endTime: '17:00' });
                          }}
                        />
                      </Flex>

                      {/* Existing Slots */}
                      {slots.map((slot) => (
                        <Box key={slot.id} mb={1}>
                          {editingSlot === slot.id ? (
                            <Box p={3} bg="blue.50" borderRadius="md">
                              <HStack spacing={2} mb={2}>
                                <Input
                                  type="time"
                                  size="sm"
                                  value={editSlotData.startTime}
                                  onChange={(e) => setEditSlotData({ ...editSlotData, startTime: e.target.value })}
                                />
                                <Text fontSize="sm">to</Text>
                                <Input
                                  type="time"
                                  size="sm"
                                  value={editSlotData.endTime}
                                  onChange={(e) => setEditSlotData({ ...editSlotData, endTime: e.target.value })}
                                />
                              </HStack>
                              <HStack spacing={2}>
                                <IconButton
                                  icon={<CheckIcon />}
                                  size="sm"
                                  colorScheme="green"
                                  aria-label={`Save hours for ${worker.name}`}
                                  onClick={() => handleUpdateSlot(slot.id)}
                                  isLoading={saving}
                                />
                                <IconButton
                                  icon={<CloseIcon />}
                                  size="sm"
                                  variant="ghost"
                                  aria-label={`Cancel editing hours for ${worker.name}`}
                                  onClick={() => {
                                    setEditingSlot(null);
                                    setEditSlotData({ startTime: '', endTime: '' });
                                  }}
                                />
                              </HStack>
                            </Box>
                          ) : (
                            <Flex justify="space-between" align="center" p={2} bg="white" borderRadius="md">
                              <Text fontSize="sm">
                                {convertTo12Hour(slot.start_time)} - {convertTo12Hour(slot.end_time)}
                              </Text>
                              <HStack spacing={1}>
                                <IconButton
                                  icon={<EditIcon />}
                                  size="xs"
                                  colorScheme="blue"
                                  variant="ghost"
                                  aria-label={`Edit ${day} hours for ${worker.name}`}
                                  onClick={() => {
                                    setEditingSlot(slot.id);
                                    setEditSlotData({
                                      startTime: slot.start_time,
                                      endTime: slot.end_time
                                    });
                                  }}
                                />
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="xs"
                                  colorScheme="red"
                                  variant="ghost"
                                  aria-label={`Delete ${day} hours for ${worker.name}`}
                                  onClick={() => onDeleteSlot(worker.id, slot.id)}
                                />
                              </HStack>
                            </Flex>
                          )}
                        </Box>
                      ))}

                      {/* Add Slot Form */}
                      {editingAvailability?.workerId === worker.id && editingAvailability?.day === dayIndex && (
                        <Box p={3} bg="blue.50" borderRadius="md" mt={2}>
                          <HStack spacing={2} mb={2}>
                            <Input
                              type="time"
                              size="sm"
                              value={newSlot.startTime}
                              onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                            />
                            <Text fontSize="sm">to</Text>
                            <Input
                              type="time"
                              size="sm"
                              value={newSlot.endTime}
                              onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                            />
                          </HStack>
                          <HStack spacing={2}>
                            <IconButton
                              icon={<CheckIcon />}
                              size="sm"
                              colorScheme="green"
                              aria-label={`Save new ${day} hours for ${worker.name}`}
                              onClick={() => handleAddSlot(dayIndex)}
                              isLoading={saving}
                            />
                            <IconButton
                              icon={<CloseIcon />}
                              size="sm"
                              variant="ghost"
                              aria-label={`Cancel new ${day} hours for ${worker.name}`}
                              onClick={() => setEditingAvailability(null)}
                            />
                          </HStack>
                        </Box>
                      )}

                      {slots.length === 0 && (!editingAvailability || editingAvailability.workerId !== worker.id || editingAvailability.day !== dayIndex) && (
                        <Text fontSize="xs" color="gray.500">No hours set</Text>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
